import { useMemo, useState } from 'react';
import { PRICECODE_WEIGHTCONTROLLED } from '~/common/constants/ppConstants';
import { formatPrice } from '~/common/utils/numberUtil';
import { useTranslation } from 'react-i18next';
import { getSplittedArticlePrice, sortBghArticles } from '~/features/catalog/utils/articleUtil';
import type { BghArticleDetail } from '~/common/types/bghArticleDetail';

export function useCatalogBghArticleDetail(baseArticle: BghArticleDetail, initialQuantity?: number) {
  const { i18n } = useTranslation();

  const allRawArticles = baseArticle.isZzArticle ? [baseArticle, ...baseArticle.zzArticles] : [baseArticle];
  const allArticles = sortBghArticles(allRawArticles);
  const [selectedArticleNumber, setSelectedArticleNumber] = useState<string>(() => baseArticle.articleNumber);
  const [quantity, setQuantity] = useState<number>(initialQuantity ?? 1);

  const article = allArticles.find(a => a.articleNumber === selectedArticleNumber) ?? baseArticle;
  const price = getSplittedArticlePrice(article.price);

  const calculatedTotalPriceText = useMemo(() => {
    const currentQuantity = Number.isNaN(quantity) ? 0 : quantity;

    let priceValue = article.price;

    const graduatedPriceSteps = article.graduatedPriceSteps;
    if (graduatedPriceSteps && graduatedPriceSteps.length > 0) {
      for (const step of graduatedPriceSteps) {
        const actQuantity =
          currentQuantity *
          (article.sellAmount * (article.priceCode === PRICECODE_WEIGHTCONTROLLED ? article.approxWeight! : 1));
        if (actQuantity >= step.quantity) {
          priceValue = step.price;
        }
      }
    }

    const total =
      currentQuantity *
      (article.sellAmount *
        (article.priceCode === PRICECODE_WEIGHTCONTROLLED ? article.approxWeight! : 1) *
        priceValue);

    return `CHF ${formatPrice(total, i18n.language)}`;
  }, [
    article.approxWeight,
    article.graduatedPriceSteps,
    article.price,
    article.priceCode,
    article.sellAmount,
    i18n.language,
    quantity
  ]);

  return {
    ...article,
    price,
    setSelectedArticleNumber,
    allArticles,
    calculatedTotalPriceText,
    quantity,
    setQuantity
  };
}
